import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar as CalendarIcon, Clock, ShoppingCart, Trash2, TrendingUp } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    window.addEventListener('cartUpdated', updateCartCount);
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const updateCartCount = () => {
    const cart = JSON.parse(localStorage.getItem('serviceCart') || '[]');
    setCartCount(cart.length);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <img src="/cleo-logo.png" alt="Cleo Professionals" style={{ height: '100px', width: 'auto' }} />
        </Link>
        
        <ul className="navbar-menu">
          <li><Link to="/" className="navbar-link">Home</Link></li>
          <li><Link to="/services" className="navbar-link">Services</Link></li>
          <li><Link to="/gallery" className="navbar-link">Gallery</Link></li>
          <li><Link to="/booking" className="navbar-cta blink-animation">Book Now</Link></li>
        </ul>
        
        {/* Cart with Text - Before Hamburger */}
        <Link 
          to="/booking" 
          className="cart-with-label"
          style={{ 
            position: 'relative',
            marginLeft: '2rem',
            padding: '0.5rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'linear-gradient(135deg, #d4a574, #c89b64)',
            borderRadius: '8px',
            textDecoration: 'none',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(212, 165, 116, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span style={{ 
            color: 'white', 
            fontWeight: '600',
            fontSize: '1rem',
            letterSpacing: '0.5px'
          }} className="blink-animation">
            Cart
          </span>
          <div style={{ position: 'relative' }}>
            <ShoppingCart size={20} color="white" />
            {cartCount > 0 && (
              <span className="cart-badge-red" style={{
                background: '#ff4444',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: '700',
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                border: '2px solid white'
              }}>
                {cartCount}
              </span>
            )}
          </div>
        </Link>
        
        <button 
          className={`mobile-menu-btn ${mobileMenuOpen ? 'hidden' : ''}`}
          onClick={toggleMobileMenu}
        >
          ☰
        </button>
      </div>
      
      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'mobile-menu-open' : ''}`} onClick={closeMobileMenu}>
        <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
          <div className="mobile-menu-header">
            <img src="/cleo-logo.png" alt="Cleo Professionals" style={{ height: '60px', width: 'auto' }} />
            <button className="mobile-menu-close" onClick={closeMobileMenu}>✕</button>
          </div>
          <ul className="mobile-menu-list">
            <li><Link to="/" className="mobile-menu-link" onClick={closeMobileMenu}>HOME</Link></li>
            <li><Link to="/services" className="mobile-menu-link" onClick={closeMobileMenu}>Services</Link></li>
            <li><Link to="/gallery" className="mobile-menu-link" onClick={closeMobileMenu}>Gallery</Link></li>
            <li>
              <Link to="/booking" className="mobile-menu-link" onClick={closeMobileMenu} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                <ShoppingCart size={18} className="blink-animation" />
                Cart
                {cartCount > 0 && (
                  <span className="cart-badge-red" style={{
                    background: '#ff4444',
                    color: 'white',
                    borderRadius: '50%',
                    width: '22px',
                    height: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    marginLeft: '0.25rem'
                  }}>
                    {cartCount}
                  </span>
                )}
              </Link>
            </li>
            <li><Link to="/booking" className="mobile-menu-link mobile-menu-cta blink-animation" onClick={closeMobileMenu}>Book Now</Link></li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

const Booking = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [date, setDate] = useState(undefined);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    notes: ''
  });

  useEffect(() => {
    // Load services from localStorage (added from Services page)
    loadCartFromStorage();
  }, []);

  const loadCartFromStorage = () => {
    const storedCart = JSON.parse(localStorage.getItem('serviceCart') || '[]');
    if (storedCart.length > 0) {
      setCart(storedCart);
      toast.success(`${storedCart.length} service(s) loaded from your selection`);
    }
  };

  useEffect(() => {
    if (date) {
      fetchAvailableSlots(format(date, 'yyyy-MM-dd'));
    }
  }, [date]);

  const fetchAvailableSlots = async (dateStr) => {
    try {
      const response = await axios.get(`${API}/available-slots?date=${dateStr}`);
      setAvailableSlots(response.data.available_slots);
    } catch (error) {
      console.error('Error fetching available slots:', error);
      toast.error('Failed to load available time slots');
    }
  };

  const removeFromCart = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
    localStorage.setItem('serviceCart', JSON.stringify(newCart));
    window.dispatchEvent(new Event('cartUpdated'));
    toast.info('Service removed from cart');
  };

  const calculateTotal = () => {
    return cart.reduce((sum, service) => sum + service.price, 0);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      toast.error('Please add at least one service to cart');
      return;
    }
    
    if (!date) {
      toast.error('Please select a date');
      return;
    }
    
    if (!selectedTime) {
      toast.error('Please select a time slot');
      return;
    }
    
    setLoading(true);
    
    try {
      const bookingData = {
        ...formData,
        service_ids: cart.map(s => s.id),
        appointment_date: format(date, 'yyyy-MM-dd'),
        appointment_time: selectedTime
      };
      
      await axios.post(`${API}/appointments`, bookingData);
      toast.success('Appointment booked successfully!');
      
      // Reset form and clear localStorage
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        notes: ''
      });
      setCart([]);
      localStorage.removeItem('serviceCart');
      window.dispatchEvent(new Event('cartUpdated'));
      setDate(undefined);
      setSelectedTime('');
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error booking appointment:', error);
      toast.error(error.response?.data?.detail || 'Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      
      {/* Header Section */}
      <section style={{
        paddingTop: '120px',
        paddingBottom: '60px',
        background: 'linear-gradient(135deg, #fef5e7 0%, #f9f0e8 100%)',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2rem' }}>
          <span className="section-badge">Book Appointment</span>
          <h1 className="section-title" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            Schedule Your Visit
          </h1>
          <p className="section-subtitle">
            Select services, choose date & time, and book your appointment
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section style={{ padding: '4rem 2rem', background: '#fafafa' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <form onSubmit={handleSubmit}>
            <div>
              {/* Cart, Date/Time & Form */}
              <div>
                {/* Cart Summary */}
                <div style={{
                  background: 'white',
                  borderRadius: '15px',
                  padding: '1.5rem',
                  border: '1px solid #f0f0f0',
                  marginBottom: '1.5rem'
                }}>
                  <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#2c3e50' }}>
                    <ShoppingCart size={20} style={{ display: 'inline', marginRight: '0.5rem' }} />
                    Cart ({cart.length})
                  </h3>
                  
                  {cart.length === 0 ? (
                    <p style={{ color: '#5a6c7d', textAlign: 'center', padding: '2rem 0' }}>
                      No services added yet. <Link to="/services" style={{ color: '#d4a574', textDecoration: 'underline' }}>Browse Services</Link>
                    </p>
                  ) : (
                    <>
                      <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '1rem' }}>
                        {cart.map((service, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '0.75rem',
                              background: '#fafafa',
                              borderRadius: '8px',
                              marginBottom: '0.5rem'
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#2c3e50' }}>
                                {service.name}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#5a6c7d' }}>
                                ₹{service.price.toFixed(2)}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFromCart(index)}
                              data-testid={`remove-from-cart-${index}`}
                              style={{
                                padding: '0.25rem',
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                      
                      <div style={{
                        padding: '1rem',
                        background: 'linear-gradient(135deg, #d4a574, #c89b64)',
                        color: 'white',
                        borderRadius: '10px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '1.125rem', fontWeight: '600' }}>Total:</span>
                          <span style={{ fontSize: '1.5rem', fontWeight: '700' }} data-testid="cart-total">
                            ₹{calculateTotal().toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#2c3e50' }}>
                  Select Date & Time
                </h2>
                
                {/* Calendar */}
                <div style={{ 
                  background: 'white', 
                  borderRadius: '15px', 
                  padding: '1.5rem',
                  border: '1px solid #f0f0f0',
                  marginBottom: '1.5rem'
                }}>
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(date) => date < new Date() || date < new Date(new Date().setHours(0, 0, 0, 0))}
                    data-testid="booking-calendar"
                    className="rounded-md"
                  />
                </div>

                {/* Time Slots */}
                {date && (
                  <div style={{ 
                    background: 'white', 
                    borderRadius: '15px', 
                    padding: '1.5rem',
                    border: '1px solid #f0f0f0',
                    marginBottom: '1.5rem'
                  }}>
                    <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#2c3e50' }}>
                      <Clock size={20} style={{ display: 'inline', marginRight: '0.5rem' }} />
                      Select Time
                    </h3>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(3, 1fr)', 
                      gap: '0.5rem' 
                    }}>
                      {availableSlots.map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          data-testid={`time-slot-${slot}`}
                          onClick={() => setSelectedTime(slot)}
                          style={{
                            padding: '0.75rem',
                            borderRadius: '8px',
                            border: selectedTime === slot ? '2px solid #d4a574' : '1px solid #f0f0f0',
                            background: selectedTime === slot ? 'rgba(212, 165, 116, 0.1)' : 'white',
                            color: selectedTime === slot ? '#d4a574' : '#2c3e50',
                            fontWeight: selectedTime === slot ? '600' : '500',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            fontSize: '0.875rem'
                          }}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                    <p style={{ fontSize: '0.75rem', color: '#5a6c7d', marginTop: '0.5rem', textAlign: 'center' }}>
                      Multiple bookings per slot allowed
                    </p>
                  </div>
                )}

                {/* Personal Information */}
                <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', marginTop: '2rem', color: '#2c3e50' }}>
                  3. Your Information
                </h2>
                <div style={{ 
                  background: 'white', 
                  borderRadius: '15px', 
                  padding: '1.5rem',
                  border: '1px solid #f0f0f0'
                }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#2c3e50' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      required
                      data-testid="customer-name"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #f0f0f0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#2c3e50' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="customer_email"
                      value={formData.customer_email}
                      onChange={handleInputChange}
                      required
                      data-testid="customer-email"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #f0f0f0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#2c3e50' }}>
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleInputChange}
                      required
                      data-testid="customer-phone"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #f0f0f0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#2c3e50' }}>
                      Special Notes (Optional)
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="3"
                      data-testid="customer-notes"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #f0f0f0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || cart.length === 0}
                  data-testid="submit-booking"
                  className="btn-primary"
                  style={{
                    width: '100%',
                    marginTop: '1.5rem',
                    padding: '1rem',
                    fontSize: '1.125rem',
                    opacity: (loading || cart.length === 0) ? 0.6 : 1,
                    cursor: (loading || cart.length === 0) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Booking...' : `Book Now - ₹${calculateTotal().toFixed(2)}`}
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Booking;
